import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Code2, Key, Webhook, Database, Shield, Zap, CreditCard, ShoppingCart, Star, Package, Utensils, BarChart3, Bot } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BASE_URL = "https://hamhqxpknbxiuchdwxup.supabase.co/functions/v1";

const endpointGroups = [
  {
    label: "Pedidos",
    icon: ShoppingCart,
    endpoints: [
      {
        method: "POST",
        path: "/create-order",
        desc: "Cria um novo pedido com itens, endereço de entrega, cupom e método de pagamento.",
        body: {
          restaurant_id: "uuid",
          customer_name: "string",
          customer_phone: "string",
          customer_id: "string | null",
          order_type: "'dine_in' | 'takeout' | 'delivery'",
          table_number: "number | null",
          delivery_address: "string | null",
          payment_method: "'pix' | 'credit_card' | 'debit_card' | 'cash'",
          notes: "string | null",
          items: "[{ product_id, product_name, quantity, unit_price, notes }]",
          discount: "number",
          delivery_fee: "number",
          coupon_id: "string | null",
          loyalty_redemption: "{ loyalty_program_id, amount } | null",
        },
        response: '{ "order_id": "uuid", "total": 89.90 }',
      },
      {
        method: "POST",
        path: "/get-order-tracking",
        desc: "Retorna status atual do pedido, itens e informações de entrega em tempo real.",
        body: { order_id: "uuid" },
        response: '{ "id": "...", "status": "preparing", "items": [...], "delivery": {...} }',
      },
    ],
  },
  {
    label: "Pagamentos",
    icon: CreditCard,
    endpoints: [
      {
        method: "POST",
        path: "/process-mp-payment",
        desc: "Processa pagamento via Mercado Pago (Cartão de Crédito ou PIX). Suporta credenciais por restaurante ou conta global.",
        body: {
          order_id: "uuid",
          restaurant_id: "uuid",
          payment_type: "'credit_card' | 'pix'",
          token: "string (para cartão)",
          installments: "number",
          payer_email: "string",
        },
        response: '{ "success": true, "payment_id": "...", "status": "approved", "pix_qr_code": "..." }',
      },
      {
        method: "POST",
        path: "/mp-webhook",
        desc: "Webhook do Mercado Pago para notificações assíncronas de pagamento (payment.created, payment.updated).",
        body: { type: "'payment'", action: "'payment.created'", data: "{ id: number }" },
        response: '{ "ok": true }',
      },
      {
        method: "POST",
        path: "/create-checkout",
        desc: "Cria sessão de checkout para assinatura de plano da plataforma com suporte a cupom de desconto.",
        body: {
          plan_id: "uuid",
          restaurant_id: "uuid",
          billing_cycle: "'monthly' | 'yearly'",
          coupon_code: "string | null",
        },
        response: '{ "checkout_url": "https://...", "preference_id": "..." }',
      },
    ],
  },
  {
    label: "Avaliações",
    icon: Star,
    endpoints: [
      {
        method: "POST",
        path: "/submit-rating",
        desc: "Submete avaliação de um pedido (comida, serviço e entrega de 1 a 5 estrelas + comentário).",
        body: {
          order_id: "uuid",
          food_rating: "number (1-5)",
          service_rating: "number (1-5)",
          delivery_rating: "number | null (1-5)",
          comment: "string | null",
        },
        response: '{ "success": true, "rating_id": "uuid" }',
      },
    ],
  },
  {
    label: "iFood",
    icon: Utensils,
    endpoints: [
      {
        method: "POST",
        path: "/ifood-sync-catalog",
        desc: "Sincroniza o cardápio do restaurante com o iFood. Requer credenciais OAuth configuradas.",
        body: { restaurant_id: "uuid" },
        response: '{ "synced": true, "products_synced": 42 }',
      },
      {
        method: "POST",
        path: "/ifood-sync-orders",
        desc: "Importa pedidos recentes do iFood e cria os pedidos correspondentes na plataforma.",
        body: { restaurant_id: "uuid" },
        response: '{ "synced": true, "orders_imported": 5 }',
      },
    ],
  },
  {
    label: "IA & Conteúdo",
    icon: Bot,
    endpoints: [
      {
        method: "POST",
        path: "/generate-description",
        desc: "Gera descrição otimizada para um produto usando IA (Gemini Flash).",
        body: { product_name: "string", category: "string | null" },
        response: '{ "description": "Deliciosa pizza artesanal..." }',
      },
      {
        method: "POST",
        path: "/suggest-combos",
        desc: "Sugere combos e promoções inteligentes baseados no cardápio do restaurante.",
        body: { restaurant_id: "uuid" },
        response: '{ "combos": [{ "name": "...", "items": [...], "suggested_price": 49.90 }] }',
      },
      {
        method: "POST",
        path: "/generate-marketing-content",
        desc: "Gera conteúdo de marketing (legendas, ideias, copy para anúncios) para o restaurante.",
        body: { restaurant_id: "uuid", type: "'caption' | 'ideas' | 'ad_copy'", platform: "'instagram' | 'facebook'" },
        response: '{ "content": "...", "hashtags": [...] }',
      },
      {
        method: "POST",
        path: "/generate-platform-marketing",
        desc: "Gera conteúdo de marketing da plataforma ViaMenu (uso interno do Super Admin).",
        body: { type: "'caption' | 'ideas' | 'video_script' | 'image'", platform: "string", features: "string[]" },
        response: '{ "content": "...", "image_url": "..." }',
      },
    ],
  },
  {
    label: "Automações",
    icon: BarChart3,
    endpoints: [
      {
        method: "POST",
        path: "/check-inactive-customers",
        desc: "Identifica clientes inativos de um restaurante para campanhas de reativação.",
        body: { restaurant_id: "uuid" },
        response: '{ "inactive_customers": [...] }',
      },
      {
        method: "POST",
        path: "/notify-trial-expiring",
        desc: "Envia notificações para restaurantes cujo período de teste está prestes a expirar.",
        body: {},
        response: '{ "notified": 12 }',
      },
    ],
  },
];

const methodColors: Record<string, string> = {
  GET: "bg-green-500/10 text-green-400 border-green-500/20",
  POST: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  PUT: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  DELETE: "bg-red-500/10 text-red-400 border-red-500/20",
};

const ApiDocs = () => {
  return (
    <div className="min-h-screen bg-background">
      <section className="pt-32 pb-16">
        <div className="container max-w-5xl">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="h-4 w-4" /> Voltar
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
              API Reference
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">API do ViaMenu</h1>
            <p className="text-lg text-muted-foreground mb-10">
              Integre seu sistema com o ViaMenu usando nossas Edge Functions. Autenticação via Bearer Token (apikey + Authorization header).
            </p>
          </motion.div>

          {/* Quick Info */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              { icon: Key, title: "Autenticação", desc: "Header Authorization: Bearer {anon_key} + apikey header para acesso público. JWT do usuário para rotas protegidas." },
              { icon: Shield, title: "Segurança", desc: "Todas as functions usam CORS, validação de entrada e Service Role Key no servidor. Dados sensíveis nunca expostos." },
              { icon: Webhook, title: "Webhooks", desc: "Endpoint /mp-webhook recebe notificações do Mercado Pago em tempo real para atualizar status de pagamentos." },
            ].map((item) => (
              <motion.div
                key={item.title}
                className="p-6 rounded-xl bg-card border border-border"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <item.icon className="h-8 w-8 text-primary mb-3" />
                <h3 className="font-semibold mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Base URL */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Database className="h-6 w-6 text-primary" /> Base URL
            </h2>
            <div className="bg-muted rounded-lg p-4 font-mono text-sm break-all">
              <span className="text-muted-foreground">Production: </span>
              <span className="text-primary">{BASE_URL}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Todas as requisições devem incluir os headers <code className="text-foreground bg-muted px-1.5 py-0.5 rounded">apikey</code> e <code className="text-foreground bg-muted px-1.5 py-0.5 rounded">Authorization</code>.
            </p>
          </div>

          {/* Endpoints by category */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Code2 className="h-6 w-6 text-primary" /> Endpoints ({endpointGroups.reduce((sum, g) => sum + g.endpoints.length, 0)} funções)
            </h2>

            <Tabs defaultValue={endpointGroups[0].label} className="w-full">
              <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1 mb-6">
                {endpointGroups.map((group) => (
                  <TabsTrigger key={group.label} value={group.label} className="flex items-center gap-1.5 text-xs">
                    <group.icon className="h-3.5 w-3.5" />
                    {group.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {endpointGroups.map((group) => (
                <TabsContent key={group.label} value={group.label} className="space-y-4">
                  {group.endpoints.map((ep) => (
                    <motion.div
                      key={ep.path}
                      className="rounded-xl bg-card border border-border overflow-hidden"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                    >
                      <div className="p-5">
                        <div className="flex items-start gap-3 mb-3">
                          <span className={`px-2.5 py-0.5 rounded border text-xs font-bold shrink-0 ${methodColors[ep.method]}`}>
                            {ep.method}
                          </span>
                          <div>
                            <code className="text-sm font-mono text-foreground">{ep.path}</code>
                            <p className="text-sm text-muted-foreground mt-1">{ep.desc}</p>
                          </div>
                        </div>

                        {ep.body && (
                          <div className="mt-4">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Request Body</p>
                            <div className="bg-muted rounded-lg p-3 font-mono text-xs overflow-x-auto">
                              <pre className="text-muted-foreground">
                                {JSON.stringify(ep.body, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}

                        {ep.response && (
                          <div className="mt-3">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Response 200</p>
                            <div className="bg-muted rounded-lg p-3 font-mono text-xs overflow-x-auto">
                              <pre className="text-primary/80">{ep.response}</pre>
                            </div>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </TabsContent>
              ))}
            </Tabs>
          </div>

          {/* Authentication Example */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Zap className="h-6 w-6 text-primary" /> Exemplo de Requisição
            </h2>
            <div className="bg-muted rounded-xl p-6 font-mono text-sm overflow-x-auto">
              <pre className="text-muted-foreground">
{`# Criar um pedido
curl -X POST ${BASE_URL}/create-order \\
  -H "apikey: YOUR_ANON_KEY" \\
  -H "Authorization: Bearer YOUR_ANON_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "restaurant_id": "uuid-do-restaurante",
    "customer_name": "João Silva",
    "customer_phone": "(11) 99999-0000",
    "order_type": "delivery",
    "delivery_address": "Rua Exemplo, 123",
    "payment_method": "pix",
    "items": [
      {
        "product_id": "uuid-do-produto",
        "product_name": "Pizza Margherita",
        "quantity": 1,
        "unit_price": 39.90
      }
    ],
    "discount": 0,
    "delivery_fee": 5.00
  }'

# Response 200
{
  "order_id": "550e8400-e29b-41d4-a716-446655440000",
  "total": 44.90
}`}
              </pre>
            </div>
          </div>

          {/* Webhook Example */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Webhook className="h-6 w-6 text-primary" /> Configuração de Webhook (Mercado Pago)
            </h2>
            <div className="bg-card rounded-xl p-6 border border-border">
              <p className="text-sm text-muted-foreground mb-4">
                Configure a URL abaixo no painel do Mercado Pago para receber notificações de pagamento:
              </p>
              <div className="bg-muted rounded-lg p-3 font-mono text-sm break-all">
                <span className="text-primary">{BASE_URL}/mp-webhook</span>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Eventos suportados: <code className="bg-muted px-1.5 py-0.5 rounded">payment.created</code>, <code className="bg-muted px-1.5 py-0.5 rounded">payment.updated</code>
              </p>
            </div>
          </div>

          {/* SDK Info */}
          <div>
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Package className="h-6 w-6 text-primary" /> SDK & Bibliotecas
            </h2>
            <div className="bg-card rounded-xl p-6 border border-border">
              <p className="text-sm text-muted-foreground mb-4">
                A plataforma utiliza o SDK oficial do Supabase para JavaScript. Instale no seu projeto:
              </p>
              <div className="bg-muted rounded-lg p-3 font-mono text-sm mb-4">
                <code>npm install @supabase/supabase-js</code>
              </div>
              <div className="bg-muted rounded-lg p-4 font-mono text-xs overflow-x-auto">
                <pre className="text-muted-foreground">
{`import { createClient } from '@supabase/supabase-js'

const supabase = createClient(
  '${BASE_URL.replace('/functions/v1', '')}',
  'YOUR_ANON_KEY'
)

// Chamar uma Edge Function
const { data, error } = await supabase.functions.invoke('create-order', {
  body: {
    restaurant_id: 'uuid-do-restaurante',
    customer_name: 'João Silva',
    order_type: 'dine_in',
    table_number: 5,
    payment_method: 'pix',
    items: [{ product_id: '...', product_name: 'Pizza', quantity: 1, unit_price: 39.90 }],
    discount: 0,
    delivery_fee: 0
  }
})`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApiDocs;
